/**
 * Shared word entries list component.
 * Renders the same table structure as word-view-page: column headers + rows of word groups.
 * Used by word-view-page (rows only, showHeader=false) and create-entry-page (recent entries with simple header).
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { t } from '../utils/i18n.js'
import type { WordGroup, Spelling, ColumnConfig, Language, MeaningType } from '../utils/word-view-data-manager.js'
import '@tlenexkoyotl/xamix-element'
import styles from './word-entries-list.css?inline'

const XAMIX_SYSTEM_ID = '1'

@customElement('word-entries-list')
export class WordEntriesList extends LitElement {
  @property({ type: Array }) groups: WordGroup[] = []
  @property({ type: Array }) columns: ColumnConfig[] = []
  @property({ type: Array }) languages: Language[] = []
  @property({ type: Array }) meaningTypes: MeaningType[] = []
  @property({ type: Boolean }) showHeader = true
  @property({ type: String }) headerLabel = ''
  @property({ type: Boolean }) loading = false
  /** When true, columns use 1fr to fill available width (no min-width overflow). Use for create-entry-page. */
  @property({ type: Boolean }) flexColumns = false
  /** Language ID of column to make 20% wider (e.g. "1" for Nawatl with xamix). Use with flexColumns. */
  @property({ type: String }) widerColumnLanguageId = ''

  @state() private isMobile = false

  static styles = css`${unsafeCSS(styles)}`

  /** Grid template for flex columns. On mobile, use vh-based min-width so columns are wider. */
  private get flexColumnsGridTemplate(): string {
    if (!this.flexColumns || this.columns.length === 0) return ''
    const widerIndex = this.widerColumnLanguageId
      ? this.columns.findIndex(c => c.languageId === this.widerColumnLanguageId)
      : -1
    if (this.isMobile) {
      return this.columns
        .map((_, i) => (i === widerIndex ? 'minmax(30vh, 1.81fr)' : 'minmax(25vh, 1fr)'))
        .join(' ')
    }
    return this.columns
      .map((_, i) => (i === widerIndex ? '1.81fr' : '1fr'))
      .join(' ')
  }

  connectedCallback() {
    super.connectedCallback()
    const mq = window.matchMedia('(max-width: 768px)')
    this.isMobile = mq.matches
    mq.addEventListener('change', (e) => {
      this.isMobile = e.matches
    })
  }

  private updateColumnStyles() {
    this.style.setProperty('--entries-list-columns', String(this.columns.length))
    const template = this.columns
      .map(col => (col.languageId === '1'
        ? 'max-content'
        : 'minmax(var(--entries-list-column-min-width, 180px), 1fr)'))
      .join(' ')
    if (template) {
      this.style.setProperty('--entries-list-columns-template', template)
    } else {
      this.style.removeProperty('--entries-list-columns-template')
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties)
    if (changedProperties.has('columns')) {
      this.updateColumnStyles()
    }
  }

  firstUpdated() {
    this.updateColumnStyles()
  }

  private renderRow(g: WordGroup) {
    const byMeaning = new Map<string, Spelling[]>()
    for (const s of g.spellings) {
      const meaningId = s.meaning?.id ?? s.id
      if (!byMeaning.has(meaningId)) byMeaning.set(meaningId, [])
      byMeaning.get(meaningId)!.push(s)
    }
    const meaningColumns = new Map<string, Map<number, Spelling[]>>()
    for (const [meaningId, meaningSpellings] of byMeaning.entries()) {
      const columnMap = new Map<number, Spelling[]>()
      this.columns.forEach((col, colIndex) => {
        let spellingsForColumn = meaningSpellings.filter(s => s.meaning?.language?.id === col.languageId)
        if (col.meaningTypeIds.length > 0) {
          spellingsForColumn = spellingsForColumn.filter(s => col.meaningTypeIds.includes(s.meaning?.type?.id))
        }
        if (col.spellingSystemIds.length > 0) {
          spellingsForColumn = spellingsForColumn.filter(s => col.spellingSystemIds.includes(s.system.id))
        }
        if (spellingsForColumn.length > 0) columnMap.set(colIndex, spellingsForColumn)
      })
      if (columnMap.size > 0) meaningColumns.set(meaningId, columnMap)
    }

    return html`
      <li class="entry">
        <div class="columns-layout">
          ${this.columns.map((col, colIndex) => html`
            <div class="column" style="border-top: none; border-radius: 0;" ?data-first-column=${colIndex === 0} ?data-wider-column=${col.languageId === this.widerColumnLanguageId}>
              <div class="column-content">
                ${Array.from(meaningColumns.entries()).map(([meaningId, columnMap]) => {
                  const spellings = columnMap.get(colIndex) || []
                  if (spellings.length === 0) {
                    return html`<div class="column-spelling" style="min-height: 1.5rem;"></div>`
                  }
                  const allXamix = spellings.every(s => s.system.id === XAMIX_SYSTEM_ID)
                  return html`
                    <a class="meaning-link" href="/meaning-etymologies/${meaningId}" data-route="/meaning-etymologies/${meaningId}">
                      <div class="column-spelling" dir="${allXamix ? 'rtl' : 'ltr'}">
                        ${spellings.map((s, idx) => html`
                          ${s.system.id === XAMIX_SYSTEM_ID
                          ? html`<span class="xamix-rtl">
                              <xamix-element dir="rtl" text-input="${s.spelling}" root="/node_modules/@tlenexkoyotl/xamix-element" font-size="4" bold adaptable></xamix-element>
                            </span>`
                          : html`<span class="headword">${s.spelling}</span>`}
                        ${idx < spellings.length - 1 ? html`<br>` : ''}
                      `)}
                    </div>
                  </a>
                  `
                })}
              </div>
            </div>
          `)}
        </div>
      </li>
    `
  }

  render() {
    if (this.loading) {
      return html`<div class="loading">${t('common.loading')}</div>`
    }
    if (this.groups.length === 0) {
      return html`<div class="empty">${t('wordView.empty') || 'No entries'}</div>`
    }

    return html`
      ${this.showHeader ? html`
        <p class="list-header">${this.headerLabel || t('wordView.headwords')}</p>
        <div class="columns-scroll-container ${this.flexColumns ? 'flex-columns' : ''}" style="${this.flexColumns && this.flexColumnsGridTemplate ? `grid-template-columns: ${this.flexColumnsGridTemplate}` : ''}">
          <div class="columns-layout columns-header">
            ${this.columns.map((col, colIndex) => {
              const lang = this.languages.find(l => l.id === col.languageId)
              return html`<div class="column" ?data-first-column=${colIndex === 0} ?data-wider-column=${col.languageId === this.widerColumnLanguageId}><span>${lang ? t(lang.name) : col.languageId}</span></div>`
            })}
          </div>
          <ul class="list ${this.flexColumns ? 'flex-columns' : ''}" style="${this.flexColumns && this.flexColumnsGridTemplate ? `grid-template-columns: ${this.flexColumnsGridTemplate}` : ''}">${this.groups.map(g => this.renderRow(g))}</ul>
        </div>
      ` : html`
        <ul class="list ${this.flexColumns ? 'flex-columns' : ''}">${this.groups.map(g => this.renderRow(g))}</ul>
      `}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'word-entries-list': WordEntriesList
  }
}
